import type { ReportStatus } from '../../api/DigiobcanApi';
import { shortId } from '../../lib/ids';
import { __, formatDate, hasLocation } from '../../utils';
import ErbThumbnail from '../ErbThumbnail';
import { feedItemBadge } from '../Feed/feedStatus';
import NorthIcon from '@mui/icons-material/North';
import { Box, Stack, StatusChip, styled } from '@presov.sk/presov-ui-react';
import React from 'react';
import { Link } from 'react-router';

/**
 * Príloha položky feedu v podobe, akú potrebuje pás fotiek a galéria v detaile.
 * Je to zúžený tvar toho, čo vracia API (`public_url` / `media_type` /
 * `filename`) - karta z neho berie prvý obrázok, detail celý zoznam.
 */
export type FeedMedia = {
  url: string;
  type: string;
  filename?: string;
};

export type IFeedItem = {
  id: string;
  title: string;
  description: string;
  date?: string;
  image?: string;
  video?: string;
  /**
   * Všetky prílohy podnetu. Nesú sa až sem zámerne: detail v paneli z nich
   * kreslí pás náhľadov a galériu, a keby ich feed zahodil (ako to robil, kým
   * si bral len prvý obrázok), musel by si detail dotiahnuť celý podnet znova.
   * Novinky ich nemajú - tie majú jednu titulnú fotku v `image`.
   */
  media?: FeedMedia[];
  location?: {
    type: 'Point';
    coordinates: [number, number];
  };
  /**
   * `userReport` je **môj** podnet - ten istý podnet z verejného zoznamu
   * dostane tento typ, keď ho vidím ako svoj. Vlastný typ pre podnet, ktorý
   * ešte nedobehol cez `reports-processor`, tu bol a je preč zámerne: bol to
   * interný stav mašinérie a občan má vidieť podané podnety rovnako, či už
   * systém stihol svoje, alebo nie.
   */
  type: 'news' | 'report' | 'userReport';
  chips?: { label: string }[];
  votesCount?: number;
  /**
   * Stav podnetu tak, ako ho vracia API. Novinky ho nemajú. Panel na hlavnej
   * stránke z neho počíta štatistiku a filtruje - preto sa nesie až sem
   * a nezahadzuje sa v `getFeed`.
   */
  status?: ReportStatus;
};

const LinkStyled = styled(Link)``;

type Props = {
  item: IFeedItem;
  showImage: boolean;
  onClick?: (itemId: string) => void;
};

/**
 * Karta v paneli hlavnej stránky: náhľad 56 × 56, stav a dátum, názov na dva
 * riadky, dole počet hlasov a kategória. Je zámerne kompaktná a **bez popisu**
 * - popis je až v detaile, ktorý sa otvára v tom istom paneli.
 *
 * Počet hlasov je **údaj, nie tlačidlo**, hoci dizajn kreslí pilulku, ktorá
 * vyzerá klikateľne. Hlasovať sa dá na detaile podnetu; feed z API nenesie,
 * či už používateľ hlasoval, takže tlačidlo v karte by po kliknutí nevedelo
 * ukázať správny stav. Doplniť sa to dá, keď `/reports` vráti aj `has_voted`.
 */
const FeedItem: React.FC<Props> = ({ item, showImage = true, onClick }) => {
  // Podnet vlastnú stránku nemá - jeho detail žije nad mapou na adrese
  // `/podnety/<ID>` ([[HomePage]]). Novinka stránku má ďalej.
  const itemPath =
    item.type === 'news'
      ? `/novinky/${shortId(item.id)}`
      : `/podnety/${shortId(item.id)}`;
  const badge = React.useMemo(() => feedItemBadge(item), [item]);

  // Položka s polohou vedie na mapu, nie na svoju stránku: klik na kartu robí
  // to isté, čo predtým tlačidlo „Zobraziť na mape" - mapa sa presunie a otvorí
  // detail. Odkaz zostáva odkazom, len mu plnému kliknutiu vezmeme prednosť:
  // Cmd/Ctrl-klik, stredné tlačidlo aj „otvoriť na novej karte" tak stále
  // otvoria stránku položky. Bez polohy niet čo na mape ukázať, tam sa nemení
  // nič.
  const opensOnMap = !!onClick && hasLocation(item);

  return (
    <LinkStyled
      to={itemPath}
      // Podľa neho si zoznam pamätá, kde stál (`Feed`, `scrollStore`).
      data-feed-item={item.id}
      style={{ textDecoration: 'none' }}
      onClick={(event) => {
        if (!opensOnMap || event.metaKey || event.ctrlKey || event.shiftKey) {
          return;
        }
        event.preventDefault();
        onClick?.(item.id);
      }}
    >
      <Stack
        direction='row'
        tabIndex={0}
        sx={(theme) => ({
          flex: 'none',
          gap: 1.25,
          padding: 1.25,
          alignItems: 'stretch',
          borderRadius: `${theme.typography.pxToRem(12)}`,
          backgroundColor: theme.palette.background.paper,
          border: `${theme.typography.pxToRem(1)} solid ${
            theme.palette.divider
          }`,
          color: theme.palette.text.primary,
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: theme.palette.grey[50],
              borderColor: theme.palette.grey[300],
            },
          },
        })}
      >
        {showImage ? (
          <ErbThumbnail
            src={item.image}
            video={item.video}
            erbSize={28}
            sx={(theme) => ({
              flex: 'none',
              width: 56,
              height: 56,
              borderRadius: `${theme.shape.borderRadius}px`,
            })}
          />
        ) : null}

        <Stack sx={{ flex: 1, minWidth: 0, gap: 0.75 }}>
          <Stack direction='row' sx={{ alignItems: 'center', gap: 1 }}>
            <StatusChip tone={badge.tone}>{badge.label}</StatusChip>
            {item.date ? (
              <Box
                component='span'
                sx={(theme) => ({
                  fontSize: theme.typography.pxToRem(11),
                  color: theme.palette.grey[500],
                })}
              >
                {formatDate(new Date(item.date))}
              </Box>
            ) : null}
          </Stack>

          <Box
            component='span'
            sx={(theme) => ({
              fontSize: theme.typography.pxToRem(14),
              fontWeight: 700,
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            })}
          >
            {item.title}
          </Box>

          <Stack
            direction='row'
            sx={{ marginTop: 'auto', alignItems: 'center', gap: 1.25 }}
          >
            {item.type === 'news' ? null : (
              <Stack
                direction='row'
                aria-label={__('Počet hlasov')}
                sx={(theme) => ({
                  flex: 'none',
                  alignItems: 'center',
                  gap: 0.75,
                  height: theme.typography.pxToRem(26),
                  padding: theme.spacing(0, 1),
                  borderRadius: `${theme.shape.borderRadiusFull}px`,
                  border: `${theme.typography.pxToRem(1)} solid ${
                    theme.palette.divider
                  }`,
                  fontSize: theme.typography.pxToRem(12),
                  fontWeight: 700,
                })}
              >
                <NorthIcon sx={{ fontSize: 12 }} />
                {item.votesCount ?? 0}
              </Stack>
            )}

            {item.chips?.length ? (
              <Box
                component='span'
                sx={(theme) => ({
                  minWidth: 0,
                  fontSize: theme.typography.pxToRem(11),
                  color: theme.palette.grey[500],
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                })}
              >
                {item.chips.map((chip) => chip.label).join(' · ')}
              </Box>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </LinkStyled>
  );
};

export default FeedItem;
